export function templateBody(template) {
  const body = (template.components || []).find(
    (c) => String(c.type).toUpperCase() === "BODY"
  );
  return body?.text || "";
}

const PLACEHOLDER_RE = /\{\{\s*([^}]+?)\s*\}\}/g;

/**
 * Parses text to find variables and provides a mapping to positional parameters {{1}}, {{2}}...
 * Meta requires positional parameters in the template definition.
 */
export function parseTemplateText(text = "") {
  const source = String(text || "");
  const parts = [];
  const variables = []; // Raw keys like "name", "order_id"
  const seen = new Map(); // key -> positional index (1-based)
  let lastIndex = 0;
  let match;

  while ((match = PLACEHOLDER_RE.exec(source))) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", value: source.slice(lastIndex, match.index) });
    }
    const key = String(match[1] || "").trim();
    
    if (!seen.has(key)) {
      seen.set(key, seen.size + 1);
      variables.push(key);
    }
    
    const position = seen.get(key);
    parts.push({ type: "var", key, position, raw: match[0] });
    
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < source.length) {
    parts.push({ type: "text", value: source.slice(lastIndex) });
  }

  // Generate the text Meta expects: "Hello {{1}}" instead of "Hello {{name}}"
  const metaText = parts.map(p => p.type === 'var' ? `{{${p.position}}}` : p.value).join('');

  return { parts, variables, metaText, mapping: Object.fromEntries(seen) };
}

export function collectTemplateInputs(fields = {}) {
  const ordered = [];
  const pushUnique = (scope, key) => {
    if (!ordered.find((item) => item.scope === scope && item.key === key)) {
      ordered.push({ scope, key, id: `${scope}:${key}` });
    }
  };

  parseTemplateText(fields.header).variables.forEach((key) => pushUnique("header", key));
  parseTemplateText(fields.body).variables.forEach((key) => pushUnique("body", key));
  return ordered;
}

export function buildTemplateSendComponents(fields = {}, values = {}) {
  const components = [];
  const append = (type, text) => {
    const parsed = parseTemplateText(text);
    if (!parsed.variables.length) return;
    components.push({
      type: String(type || "").toUpperCase(),
      parameters: parsed.variables.map((key) => ({
        type: "text",
        text: values[`${String(type).toLowerCase()}:${key}`] ?? "",
      })),
    });
  };

  append("header", fields.header);
  append("body", fields.body);

  const firstBodyVar = parseTemplateText(fields.body).variables[0];
  const firstHeaderVar = parseTemplateText(fields.header).variables[0];
  const buttonValue =
    values[`body:${firstBodyVar}`] ??
    values[`header:${firstHeaderVar}`] ??
    "";
  const firstButton = Array.isArray(fields.buttons) ? fields.buttons[0] : null;
  const buttonType = String(firstButton?.type || "").toUpperCase();
  const needsAuthButton =
    String(fields.category || "").toUpperCase() === "AUTHENTICATION" &&
    firstButton &&
    ["COPY_CODE", "URL", "OTP"].includes(buttonType);

  if (needsAuthButton) {
    components.push({
      type: "button",
      sub_type: "url",
      index: "0",
      parameters: [{ type: "text", text: String(buttonValue || "") }],
    });
  }

  return components;
}

export function renderTemplateText(text = "", values = {}) {
  return parseTemplateText(text).parts.map((part) =>
    part.type === "var"
      ? values[part.key] || part.raw
      : part.value
  ).join("");
}

export function languageLabel(language) {
  if (!language) return "English";
  if (language === "en") return "English";
  if (language === "en_US") return "English (US)";
  return language;
}

export function buildComponents({ header, body, footer, buttons, mediaSample, examples = {} }) {
  const components = [];

  // Header Handling
  if (header || (mediaSample && mediaSample !== "NONE")) {
    const parsedHeader = parseTemplateText(header);
    const hComp = {
      type: "HEADER",
      format: mediaSample && mediaSample !== "NONE" ? mediaSample : "TEXT",
    };

    if (hComp.format === "TEXT") {
      hComp.text = parsedHeader.metaText;
      if (parsedHeader.variables.length) {
        hComp.example = {
          header_text: parsedHeader.variables.map(v => examples[`header:${v}`] || `[${v}]`)
        };
      }
    } else {
      // Media Header Example (Required by Meta)
      hComp.example = {
        header_handle: ["https://scontent.xx.fbcdn.net/v/t39.30808-6/placeholder.jpg"] 
      };
    }
    components.push(hComp);
  }

  // Body Handling
  if (body) {
    const parsedBody = parseTemplateText(body);
    const bComp = {
      type: "BODY",
      text: parsedBody.metaText,
    };
    if (parsedBody.variables.length) {
      bComp.example = {
        body_text: [parsedBody.variables.map(v => examples[`body:${v}`] || `[${v}]`)]
      };
    }
    components.push(bComp);
  }

  // Footer Handling
  if (footer) components.push({ type: "FOOTER", text: footer });

  // Buttons Handling
  if (Array.isArray(buttons) && buttons.length) {
    components.push({
      type: "BUTTONS",
      buttons: buttons.map((b) => {
        const btn = {
          type: b.type,
          text: b.text,
        };
        if (b.type === "URL") {
          const parsedUrl = parseTemplateText(b.url || "");
          btn.url = parsedUrl.metaText;
          if (parsedUrl.variables.length) {
            btn.example = [examples[`button:${b.url}`] || "https://example.com/value"];
          }
        } else if (b.type === "PHONE_NUMBER") {
          btn.phone_number = b.phone_number;
        } else if (b.type === "COPY_CODE") {
          btn.example = b.example || "PROMO20";
        }
        return btn;
      }),
    });
  }

  return components;
}

export function extractTemplateFields(template) {
  const components = template.components || [];
  const body = components.find((c) => String(c.type).toUpperCase() === "BODY");
  const footer = components.find((c) => String(c.type).toUpperCase() === "FOOTER");
  const header = components.find((c) => String(c.type).toUpperCase() === "HEADER");
  const buttons = components.find((c) => String(c.type).toUpperCase() === "BUTTONS");
  
  // Note: We can't perfectly recover the original named parameters from Meta's positional ones 
  // unless we store the mapping somewhere. For now, we'll just show positional {{1}} on edit.
  
  return {
    header: header?.format === "TEXT" ? header.text || "" : "",
    mediaSample: header?.format && header.format !== "TEXT" ? header.format : "NONE",
    body: body?.text || "",
    footer: footer?.text || "",
    buttons: buttons?.buttons || [],
    category: template?.category || "",
  };
}

